import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { BillingDetails } from 'app/shared/models/billing-details';
import { BillingService } from 'app/shared/services/billing.service';
import { AlertService } from 'app/shared/services/alert.service';
import { DacConfig } from 'app/shared/services/dac-config.service';
import { Subscription } from 'app/shared/models/subscription';
import { SubscriptionService } from '../services/subscription.service';
import { MetricsService } from 'app/shared/services/metrics.service';
import { SystemError } from 'app/shared/models/SystemError';
import { ErrorCode } from 'app/shared/models/ErrorCodes';

@Component({
  selector: 'dac-billing-plans',
  templateUrl: './billing-plans.component.html',
  styleUrls: ['./billing-plans.component.scss']
})
export class BillingPlansComponent implements OnInit {
  private billingDetails:BillingDetails;
  public loading:boolean;
  public upgrading:boolean;
  public subscriptionAlertMessage:string;

  public canceling:boolean;
  public cancelConfirm:boolean;
  public cancelFinalized:boolean;
  public cancelReason:string;

  @ViewChild("cancelOtherReason") cancelOtherReason:ElementRef;

  private readonly METRICS_CATEGORY = 'SubscriptionUpgrade';
  public cancellationReasons:{id:string,label:string}[] = [
    {id:'tooExpensive', 'label': 'Your product is too expensive'}
    ,{id:'understandHowToUse', 'label': 'I don’t understand how to use your product'}
    ,{id:'betterProduct', 'label': 'I found something else I like better'}
    ,{id:'missingKeyFeature', 'label': 'You’re missing a key feature I need'}
    ,{id:'notUsingIt', 'label': 'I don’t use it like I thought I would'}
    ,{id:'notMyDecision', 'label': 'It’s not my decision (someone else is having me cancel)'}
    ,{id:'tooBuggy', 'label': 'Your product has too many glitches or technical problems'}
    ,{id:'subscribeLater', 'label': "I'm not using this now but I plan on subscribing at a later point in time"}
    ,{id:'cantAfford', 'label': "I can't afford this right now"}
  ];

  constructor(private billingService:BillingService
  ,private alertService:AlertService
  ,private dacConfig:DacConfig
  ,private subService:SubscriptionService
  ,private metrics:MetricsService) { 
    this.billingDetails = new BillingDetails();
  }

  get hasSubscription() {
    return this.billingDetails.subscriptions.length > 0;
  }

  get stripeChargeName() {
    return this.dacConfig.stripe_charge_name;
  }

  ngOnInit() {
    this.loading = true;
    this.billingService.getBillingDetails()
    .then(details => {
      this.billingDetails = details
      this.loading = false;
    })
    .catch((error) => {
      this.loading = false;
      console.error(error);
      this.metrics.trackEvent(this.METRICS_CATEGORY, 'ngOnInit', 'failed to get billing details');
      this.alertService.error("Failed to retrieve billing details from server.  Check your network connection and refresh the page.");
    });
  }

  upgradeTrialSubscription(subscription:Subscription) {
    this.subscriptionAlertMessage = null;
    this.upgrading = true;

    // collect the payment credentials and update the card
    this.billingService.updateDefaultBillingCard(this.billingDetails)
    .then((updatedBillingDetails) => {
      this.billingDetails = updatedBillingDetails;
      // 
      this.alertService.info("Billing card saved. Upgrading Subscription...");
      this.subService.upgradeSubscription(subscription, this.billingDetails)
      .then(billingDetails => {
        this.metrics.trackEvent(this.METRICS_CATEGORY, 'upgradeTrialSubscription', 'subscription upgrade success');
        this.billingDetails = billingDetails;
        this.alertService.success("Subscription upgraded!");
        this.upgrading = false;
      })
      .catch((error:SystemError) => {
        this.upgrading = false;
        this.subscriptionAlertMessage = error.message;
        this.metrics.trackEvent(this.METRICS_CATEGORY, 'upgradeTrialSubscription', 'subscription upgrade failed ' + error.message);
      })
      // need to send subscription to server
      // end the free subscription
    })
    .catch((error) => {
      this.upgrading = false;
      if (error.code && error.code == 'canceled') {
        this.metrics.trackEvent(this.METRICS_CATEGORY, 'upgradeTrialSubscription', 'billing card update canceled by customer');
        this.subscriptionAlertMessage = "Billing card update was canceled.  A valid billing card must be provided before subscription can be upgraded";
      } else {
        console.error(error);
        this.metrics.trackEvent(this.METRICS_CATEGORY, 'upgradeTrialSubscription', 'billing card update failed');
        this.subscriptionAlertMessage = "There was an unexpected error in saving your billing card. Please try again or contact customer support";
      }
      
      this.alertService.error("There was an error in saving your billing card. Please try again or contact customer support");
    });
  }

  updatePastDueBillingDetails(subscription:Subscription) {
    this.upgrading = true;
    this.subscriptionAlertMessage = null;
    this.metrics.trackEvent(this.METRICS_CATEGORY, 'updatePastDueBillingDetails');
    this.billingService.updateDefaultBillingCard(this.billingDetails)
    .then((updatedBillingDetails) => {
      this.metrics.trackEvent(this.METRICS_CATEGORY, 'updatePastDueBillingDetails', 'Card update success');
      this.billingDetails = updatedBillingDetails;
      
      this.alertService.info("Billing card saved. Upgrading subscription payment...");
      this.subService.updatePastDueSubscription(subscription, updatedBillingDetails)
      .then((billingDetails) => {
        this.upgrading = false;
        this.billingDetails = billingDetails;
        let updatedSub = billingDetails.subscriptions.find(s => s.id == subscription.id);
        if (updatedSub && updatedSub.isUnpaidSubscription()) {
          this.metrics.trackEvent(this.METRICS_CATEGORY, 'updatePastDueBillingDetails', 'Subscription past-due');
          this.subscriptionAlertMessage = "Our card processor could not immediately charge this card.  We will continue to attempt to charge this card over the next few hours, or you can try a different card.  Most cards should process within 24 hours.  Contact your card provider or our support for more assistance.";
          
        } else if (updatedSub && updatedSub.isActiveSubscription()) {
          this.metrics.trackEvent(this.METRICS_CATEGORY, 'updatePastDueBillingDetails', 'Subscription active');
          this.alertService.success("Subscription activated!");
        } else {
          this.metrics.trackEvent(this.METRICS_CATEGORY, 'updatePastDueBillingDetails', 'Subscription state other');
          this.alertService.error("There was an error in processing your subscription upgrade.  Contact support for assistance");
        }
      })
      .catch((error) => {
        this.upgrading = false;
        this.metrics.trackEvent(this.METRICS_CATEGORY, 'updatePastDueBillingDetails', 'Subscription update failed');
        this.alertService.error("There was an error in processing your subscription upgrade.  Contact support for assistance");
        console.error(error);
      });
      // end the free subscription
    })
    .catch((error) => {
      this.upgrading = false;
      this.metrics.trackEvent(this.METRICS_CATEGORY, 'updatePastDueBillingDetails', 'Card update failed');
      console.error(error);
      this.alertService.error("There was an error in saving your billing card. Please try again or contact customer support");
    });
  }

  activateSubscription(subscription:Subscription) {
    // activate the subscription
    // TODO: stephen do we want to support this?
  }

  startCancellation(subscription:Subscription) {
    this.cancelConfirm = true;
    this.canceling = true;
    this.cancelReason = null;
  }

  cancelSubscription(subscription:Subscription) {
    if (!this.cancelReason) {
      this.alertService.error("Please provide a reason for why you are cancelling.");
      console.error("No reason was selected but cancelSubscription called");
      return;
    }
    let cancelReason = this.cancelReason;
    if (cancelReason == 'Other') {
      cancelReason = this.cancelOtherReason.nativeElement.value;
      if (cancelReason.trim() == "") {
        this.alertService.error("Please provide a reason for why you are cancelling.");
        return;
      } else {
        cancelReason = "Other: " + cancelReason;
      }
    }
    else {
      let chosenReason = this.cancellationReasons.find(cr => cr.id == cancelReason);
      if (!chosenReason) {
        this.alertService.error("Please provide a reason for why you are cancelling.");
        console.error("A reason provided, but was not in the cancellation array");
        return;
      }
      cancelReason = chosenReason.label;
    }
    

    console.log("Cancel reason is: ", cancelReason);
    this.subService.cancelSubscription(subscription, this.billingDetails, cancelReason)
    .then((billingDetails) => {
      this.billingDetails = billingDetails;
      this.cancelConfirm = false;
      this.canceling = false;
    })
    .catch((error:SystemError) => {
      console.error(error);
      this.alertService.error(error.message);
    });
  }

  keepSubscription(subscription:Subscription) {
    this.cancelConfirm = false;
    this.canceling = false;
  }

  isCancelChecked(reason:string) {
    return this.cancelReason == reason;
  }

  setCancelReason(reason:string) {
    this.cancelReason = reason;
  }

  hasCancelReason() {
    return this.cancelReason !== null;
  }

  getPlanItems(subscription:Subscription) {
    return this.subService.getPlanDetailsForSubscription(subscription);
  }

  get Billing() {
    return this.billingDetails;
  }

  get supportEmail() {
    return this.dacConfig.supportEmail;
  }

}



// WEBPACK FOOTER //
// ./src/app/admin/billing-plans/billing-plans.component.ts